import { useEffect, useRef, useState, useCallback } from 'react';
import { useReducedMotion as fmUseReducedMotion } from 'framer-motion';

/**
 * Apple AirPods-grade motion primitives.
 * GPU-only (transform/opacity), hydration-safe for react-snap.
 */

// Global easing tokens (matches design_guidelines.json)
export const EASING = {
  appleSnappy: [0.25, 1, 0.5, 1],
  appleSmooth: [0.42, 0, 0.58, 1],
  cinematicSlow: [0.22, 1, 0.36, 1],
  magneticSnap: [0.175, 0.885, 0.32, 1.275],
};

/** Honors prefers-reduced-motion (re-exported for convenience). */
export const useReducedMotion = fmUseReducedMotion;

/** Returns true after first client-side mount — keeps SSR/static HTML in sync. */
export const useIsMounted = () => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  return mounted;
};

/**
 * Magnetic hover: element gently follows the cursor on hover.
 * Returns { ref, style } — spread style onto a motion.div.
 */
export const useMagneticHover = (strength = 0.25) => {
  const ref = useRef(null);
  const reduced = fmUseReducedMotion();
  const [t, setT] = useState({ x: 0, y: 0 });

  const onMouseMove = useCallback(
    (e) => {
      if (reduced || !ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const x = (e.clientX - rect.left - rect.width / 2) * strength;
      const y = (e.clientY - rect.top - rect.height / 2) * strength;
      setT({ x, y });
    },
    [reduced, strength]
  );

  const onMouseLeave = useCallback(() => setT({ x: 0, y: 0 }), []);

  return { ref, onMouseMove, onMouseLeave, x: t.x, y: t.y };
};

/**
 * Stagger reveal: parent + child variants for sequential entrance.
 * Use as `variants={parent}` on container, `variants={child}` on each item.
 */
export const staggerParent = (stagger = 0.08, delayChildren = 0.05) => ({
  hidden: {},
  visible: { transition: { staggerChildren: stagger, delayChildren } },
});

export const staggerChild = (distance = 24) => ({
  hidden: { opacity: 0, y: distance },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: EASING.appleSmooth },
  },
});

/**
 * Count-up animation — animates a number from 0 → target when in view.
 * Returns the current displayed value.
 */
export const useCountUp = (target, duration = 1.6, inView = true) => {
  const [value, setValue] = useState(0);
  const reduced = fmUseReducedMotion();
  useEffect(() => {
    if (!inView) return;
    if (reduced) {
      setValue(target);
      return;
    }
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min((now - start) / (duration * 1000), 1);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(target * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, inView, reduced]);
  return value;
};
